// Relatórios por EAP da projeção financeira (menu "Baixar relatório"): cada curva sai
// do `/project/getReport` como `{ wbs, data: [{ month, year, value }] }`, com o código
// completo da EAP (construtora + obra + níveis). Aqui viram linhas de planilha com o
// código só da EAP (`001.01.001…`, ver `formatWbsCode`).
import { formatWbsCode, wbsDepth } from "@utils/wbsCode";

const monthKey = ({ month, year }) => `${month}/${year}`;

// "1/2026" -> "01/2026", para o cabeçalho.
const monthLabel = (month) => month.padStart(7, "0");

const ROOT_LABEL = "Total da obra";

// Meses presentes nos relatórios, em ordem cronológica ("M/AAAA").
export const reportMonths = (reports) => [...new Set(
  reports.flatMap(res => res.items.flatMap(i => i.data.map(monthKey)))
)].sort((a, b) => {
  const [monthA, yearA] = a.split("/").map(Number);
  const [monthB, yearB] = b.split("/").map(Number);
  return yearA - yearB || monthA - monthB;
});

// Nome de cada nó da EAP; a própria obra não está entre os itens e vira "Total da obra".
const wbsNames = (buildingItems = [], roots) => {
  const names = new Map(buildingItems.map(item => [item.wbs_code_id, item.description || item.building_unit_name]));
  roots.forEach(root => names.set(root, ROOT_LABEL));
  return names;
};

// Valores em centavos: o banco guarda somas em ponto flutuante (722086.7942679999).
const roundMoney = (value) => Math.round(value * 100) / 100;

// Valor de cada EAP em cada mês, por curva. Quando a mesma EAP vem repetida, vale a
// primeira ocorrência do mês (como era no relatório por curva).
const valuesByWbs = (items) => {
  const byWbs = new Map();
  items.forEach(i => {
    const byMonth = byWbs.get(i.wbs) ?? new Map();
    i.data.forEach(point => {
      const key = monthKey(point);
      if (!byMonth.has(key)) byMonth.set(key, roundMoney(Number(point.value) || 0));
    });
    byWbs.set(i.wbs, byMonth);
  });
  return byWbs;
};

// O código guardado carrega construtora e obra; ordenar por ele mantém a árvore
// (pai antes dos filhos), porque cada nível tem largura fixa.
const sortedCodes = (codes) => [...new Set(codes)].sort((a, b) => a.localeCompare(b));

export const wbsReportFields = (months) => [
  { key: 'wbs', label: "EAP" },
  { key: 'description', label: "Nome" },
  ...months.map(month => ({ key: month, label: monthLabel(month) })),
];

// Uma linha por EAP com o valor de cada mês (0 quando a EAP não tem valor no mês).
export const wbsReportRows = (items, buildingItems, months, roots) => {
  const names = wbsNames(buildingItems, roots);
  const values = valuesByWbs(items);
  return sortedCodes(items.map(i => i.wbs)).map(code => {
    const row = { wbs: formatWbsCode(code, roots), description: names.get(code) ?? '' };
    months.forEach(month => { row[month] = values.get(code).get(month) ?? 0; });
    return row;
  });
};

// Relatório geral completo: uma aba só. Linha 1 com os meses mesclados sobre as
// curvas (e "EAP"/"Descrição" mesclados com a linha 2), linha 2 com o nome das curvas,
// depois a EAP completa na ordem da árvore, começando pelo total da obra. O grupo
// "Total" no fim soma o período de cada curva.
export const fullReportSheet = (reports, buildingItems, months, roots) => {
  const labels = reports.map(({ label }) => label);
  const width = labels.length;
  const names = wbsNames(buildingItems, roots);
  const values = reports.map(({ res }) => valuesByWbs(res.items));
  const root = roots[0];

  // O total da obra vem da linha da própria obra; se a curva não a trouxer, soma as unidades.
  const rootValues = values.map(byWbs => {
    if (!root || byWbs.has(root)) return byWbs.get(root);
    const sum = new Map();
    byWbs.forEach((byMonth, code) => {
      if (wbsDepth(code, roots) !== 1) return;
      byMonth.forEach((value, month) => sum.set(month, (sum.get(month) ?? 0) + value));
    });
    sum.forEach((value, month) => sum.set(month, roundMoney(value)));
    return sum;
  });
  const codes = sortedCodes([
    ...(root ? [root] : []),
    ...reports.flatMap(({ res }) => res.items.map(i => i.wbs)),
  ]);

  const groupHeader = (title) => [title, ...Array(width - 1).fill("")];
  const header1 = ["EAP", "Descrição", ...months.flatMap(month => groupHeader(monthLabel(month))), ...groupHeader("Total")];
  const header2 = ["", "", ...months.flatMap(() => labels), ...labels];

  const body = codes.map(code => {
    const depth = wbsDepth(code, roots);
    const indent = depth > 1 ? "  ".repeat(depth - 1) : "";
    const bySeries = values.map((byWbs, index) => (code === root ? rootValues[index] : byWbs.get(code)) ?? new Map());
    const monthly = months.flatMap(month => bySeries.map(byMonth => byMonth.get(month) ?? 0));
    const totals = bySeries.map(byMonth => roundMoney(months.reduce((sum, month) => sum + (byMonth.get(month) ?? 0), 0)));
    return [formatWbsCode(code, roots), indent + (names.get(code) ?? ""), ...monthly, ...totals];
  });

  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
    { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
    ...(width > 1 ? [...months, "Total"].map((_, group) => ({
      s: { r: 0, c: 2 + group * width },
      e: { r: 0, c: 2 + group * width + width - 1 },
    })) : []),
  ];
  const cols = [{ wch: 20 }, { wch: 45 }, ...Array((months.length + 1) * width).fill({ wch: 14 })];

  return { name: "Completo por EAP", rows: [header1, header2, ...body], merges, cols };
};
